import ReactDOM from "react-dom";
import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { encodeRefNum } from "../utils/refNumCrypto";
import { withCountryParam } from "../utils/caseHref";
import {
  getApiGeneration,
  getAssessmentList,
  searchCases,
  getAssessmentReport,
} from "../services/apiService";
import { getStatusInfo, getStatusInfoFor, formatDateTime } from "../utils/statusMapping";
import WarningsModal from "../components/WarningsModal";
import { usePageConfig } from "../hooks/usePageConfig";

// Local asset imports - Vehicle images
import popupImage from "../assets/images/cases/popup-image.png";

// Local asset imports - Icons
import latviaFlag from "../assets/icons/cases/latvia-flag.svg";
import lithuaniaFlag from "../assets/icons/cases/lithuania-flag.svg";
import estoniaFlag from "../assets/icons/cases/estonia-flag.svg";
import loadingIcon from "../assets/icons/cases/loading.svg";
import chevronDownFigma from "../assets/icons/cases/chevron-down-figma.svg";
import searchIcon from "../assets/icons/cases/search.svg";
import linkIcon from "../assets/icons/cases/link.svg";
import { OnboardingTour, TOUR_STEPS } from "../onboarding";
import { getSourceName } from "../services/authService";
import { getClientEntry, getMediaBaseUrl } from "../config/clientConfig";
import { placeholderFor } from "../utils/caseThumbnail";
import CaseThumb from "../components/CaseThumb";
import { withinListRange } from "../utils/defaultDateRange";

const collectWarningMessages = (warningData) => {
  if (!warningData) return [];

  if (typeof warningData === "string") {
    return [warningData];
  }

  if (Array.isArray(warningData)) {
    return warningData.flatMap(collectWarningMessages);
  }

  if (typeof warningData !== "object") {
    return [];
  }

  const warningSource = warningData.warning || warningData;
  const message =
    warningSource.warning_message ||
    warningSource.warningMessage ||
    warningSource.message ||
    warningSource["warning message"];

  if (message) {
    return collectWarningMessages(message);
  }

  return Object.values(warningData).flatMap(collectWarningMessages);
};

// Flag components using local SVG assets
const LvLatvia = () => (
  <img
    src={latviaFlag}
    alt="Latvia flag"
    className="w-full h-full object-cover"
  />
);

const LtLithuania = () => (
  <img
    src={lithuaniaFlag}
    alt="Lithuania flag"
    className="w-full h-full object-cover"
  />
);

const EeEstonia = () => (
  <img
    src={estoniaFlag}
    alt="Estonia flag"
    className="w-full h-full object-cover"
  />
);

// WARNING_CODES moved inside component — derived from dynamic config

function getLast30DayRange() {
  const today = new Date();
  const from = new Date(today);
  from.setDate(from.getDate() - 30);
  return {
    startDate: from.toISOString().split("T")[0],
    endDate: today.toISOString().split("T")[0],
  };
}

export default function Cases() {
  console.log("🏁 Cases component mounting/rendering...");

  const { pageConfig, clientConfig } = usePageConfig();
  const filtersConfig = pageConfig.filters || {};
  const tableColumns = pageConfig.table?.columns || {};

  /**
   * Fields the client has no data for, hidden whatever the config record says.
   *
   * The column gates below read `enabled !== false`, so a portal-config record
   * that simply omits a column — or states it enabled — shows it. The record
   * is merged OVER the static defaults (usePageConfig), so the shared
   * `country.enabled: false` loses to any record that mentions it, and Hegic
   * got a COUNTRY column reading "Unknown" on every row: its list carries no
   * country, and the request never asks for one.
   *
   * `hiddenListFields` is the in-repo per-client fact, so it wins here, for the
   * same reason tab overrides do — a remote record goes stale silently and
   * cannot know about a key added after it was seeded. The redesigned list
   * already resolves it this way.
   */
  const hiddenListFields = new Set(
    getClientEntry(getSourceName())?.hiddenListFields || [],
  );
  const showCountryColumn =
    !hiddenListFields.has("country") && tableColumns.country?.enabled !== false;
  const showCountryFilter =
    !hiddenListFields.has("country") && filtersConfig.country?.enabled !== false;
  const showWarningsColumn =
    !hiddenListFields.has("warnings") && tableColumns.warnings?.enabled !== false;

  /**
   * Filters the client cannot usefully drive, with the column left alone —
   * a row can carry warnings worth showing while the filter over them is
   * meaningless, because the codes it offers are not every client's.
   */
  const hiddenListFilters = new Set(
    getClientEntry(getSourceName())?.hiddenListFilters || [],
  );
  const showWarningsFilter =
    showWarningsColumn &&
    !hiddenListFilters.has("warnings") &&
    filtersConfig.warnings?.enabled !== false;

  /**
   * Column headings the client renames.
   *
   * The shared config's headings describe a vehicle. A property claim has no
   * plate and no vehicle, so those two would be labelling the wrong thing —
   * declared per client as `listColumnLabels`, matching the redesigned list.
   * Uppercased here because this header's other labels are.
   */
  const columnLabel = (key, fallback) => {
    const override = getClientEntry(getSourceName())?.listColumnLabels?.[key];
    if (override) return override.toUpperCase();
    return tableColumns[key]?.label || fallback;
  };
  // Warnings a client's reviewers don't want offered in the dropdown at all
  // (still shown on a row if a case happens to carry one — this only trims
  // what's selectable, per client, without touching the shared options list
  // every other client reads from).
  const hiddenWarningCodes = new Set(
    getClientEntry(getSourceName())?.hiddenWarningCodes || [],
  );
  const WARNING_CODES = (filtersConfig.warnings?.options || []).filter(
    (warning) => !hiddenWarningCodes.has(warning.code),
  );

  const navigate = useNavigate();

  // Some clients' lists carry no image at all — Hegic's rows are id, ref_num,
  // claim_state and created_at, nothing more — so those rows fetch their own.
  const selfFetchesThumbnail = Boolean(
    getClientEntry(getSourceName())?.listThumbnail,
  );

  const [loading, setLoading] = useState(true);
  const [cases, setCases] = useState([]);
  const [filteredCases, setFilteredCases] = useState([]);
  const [showPopup, setShowPopup] = useState(false);
  const [showAllWarningsModal, setShowAllWarningsModal] = useState(false);
  const [allWarningsData, setAllWarningsData] = useState([]);

  // 🔧 PAGINATION MODE TOGGLE 🔧
  // Set to true for INFINITE SCROLL, false for PAGINATION with prev/next buttons
  const [useInfiniteScroll, setUseInfiniteScroll] = useState(true); // Change to false to use pagination

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(15); // Fixed at 15 items per page
  const [totalPages, setTotalPages] = useState(1);
  const [allCases, setAllCases] = useState([]); // Store all cases for client-side pagination

  // Infinite scroll states
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMoreData, setHasMoreData] = useState(true);
  const [newCasesIds, setNewCasesIds] = useState([]);
  const currentPageRef = useRef(1); // Use ref to track current page for API calls

  // Filter states
  const [selectedStatus, setSelectedStatus] = useState("All");
  const [selectedCountry, setSelectedCountry] = useState("All");
  const [selectedWarning, setSelectedWarning] = useState("All");
  const [startDate, setStartDate] = useState(() => getLast30DayRange().startDate);
  const [endDate, setEndDate] = useState(() => getLast30DayRange().endDate);
  const [searchQuery, setSearchQuery] = useState("");
  const [isSearchMode, setIsSearchMode] = useState(false);

  // Dropdown states
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);
  const [showWarningDropdown, setShowWarningDropdown] = useState(false);
  const [showDatePicker, setShowDatePicker] = useState(false);

  // Assessment report states
  const [reportData, setReportData] = useState({
    totalInspections: 0,
    completedInspections: 0,
    incorrectImages: 0,
  });
  const [reportLoading, setReportLoading] = useState(false);

  // Refs for click outside detection
  const statusDropdownRef = useRef(null);
  const countryDropdownRef = useRef(null);
  const warningDropdownRef = useRef(null);
  const selectedStatusRef = useRef("All");
  const selectedCountryRef = useRef("All");
  const selectedWarningRef = useRef("All");
  const datePickerRef = useRef(null);
  const scrollContainerRef = useRef(null);

  // Polling ref for processing cases
  const pollingIntervalRef = useRef(null);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        statusDropdownRef.current &&
        !statusDropdownRef.current.contains(event.target)
      ) {
        setShowStatusDropdown(false);
      }
      if (
        countryDropdownRef.current &&
        !countryDropdownRef.current.contains(event.target)
      ) {
        setShowCountryDropdown(false);
      }
      if (
        warningDropdownRef.current &&
        !warningDropdownRef.current.contains(event.target)
      ) {
        setShowWarningDropdown(false);
      }
      if (
        datePickerRef.current &&
        !datePickerRef.current.contains(event.target)
      ) {
        setShowDatePicker(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Helper functions with useCallback to fix dependency warning

  const getVehicleImageFromAPI = useCallback(
    (assessment) => {
      // Resolved fresh from localStorage rather than the `clientConfig` this
      // component closes over: that value comes from usePageConfig(), which
      // swaps in the portal-config API's client record once it answers, and
      // this callback's empty dependency array below would have kept using
      // whichever value existed at mount forever either way. getMediaBaseUrl()
      // prefers the in-repo per-client file over that same API record, for
      // the reason CLAUDE.md gives for every other client-config read here: a
      // remote record seeded before a field existed cannot know about it.
      const baseUrl = getMediaBaseUrl();

      // Each side value can be an object {picture, upload_picture} or an array of such objects
      const getImageUrl = (sideValue) => {
        const obj = Array.isArray(sideValue) ? sideValue[0] : sideValue;
        if (!obj) return null;
        const imagePath = obj.picture || obj.upload_picture;
        if (!imagePath) return null;
        if (/^https?:\/\//i.test(imagePath)) return imagePath;
        const normalizedPath = imagePath.startsWith("/") ? imagePath : "/" + imagePath;
        return baseUrl + normalizedPath;
      };


      if (assessment?.car_images) {
        const priorityOrder = ["front", "frontLeft", "frontRight", "left", "right", "rear", "rearLeft", "rearRight"];
        for (const side of priorityOrder) {
          if (assessment.car_images[side]) {
            const imageUrl = getImageUrl(assessment.car_images[side]);
            if (imageUrl) return imageUrl;
          }
        }
        // Try any remaining side
        for (const side of Object.keys(assessment.car_images)) {
          const imageUrl = getImageUrl(assessment.car_images[side]);
          if (imageUrl) return imageUrl;
        }
      }

      /**
       * No photograph on the case means no photograph shown.
       *
       * This hashed the case id into one of seven stock car photographs, which
       * does not read as "no image" — it reads as this claim's vehicle.
       */
      return placeholderFor(getClientEntry(getSourceName())?.inspectionLine);
    },
    [],
  );

  const formatCaseId = useCallback((caseId) => {
    if (!caseId || caseId === "N/A") return "N/A";

    // Return the full case ID without truncation
    return caseId;
  }, []);

  // New function to format display ID - prioritize vehicle registration number
  const formatDisplayId = useCallback((assessment) => {
    // Use num_plate as the primary field for vehicle registration number
    if (
      assessment.num_plate &&
      assessment.num_plate !== "N/A" &&
      assessment.num_plate.trim() !== ""
    ) {
      return assessment.num_plate.trim();
    }

    // Fallback to timestamp if no registration number found
    return (
      formatDateTime(assessment.created_at) || assessment.created_at || "N/A"
    );
  }, []);

  const getStatusIcon = useCallback((status) => {
    const statusInfo = getStatusInfo(status);
    return statusInfo.icon;
  }, []);

  const getStatusColor = useCallback((status) => {
    const statusInfo = getStatusInfo(status);
    return statusInfo.color;
  }, []);

  const getWarnings = useCallback((assessment) => {
    return collectWarningMessages(assessment?.all_warning_list)
      .map((message) => message.trim())
      .filter(Boolean)
      .map((message) => ({ message }));
  }, []);

  const getCountryFlag = useCallback((countryName) => {
    const country = countryName?.toLowerCase().trim();

    switch (country) {
      case "latvia":
      case "lv":
      case "lat":
        return <LvLatvia />;
      case "lithuania":
      case "lt":
      case "ltu":
        return <LtLithuania />;
      case "estonia":
      case "ee":
      case "est":
        return <EeEstonia />;
      default:
        console.log("No flag match found for country:", countryName);
        return <LvLatvia />; // Default flag
    }
  }, []);

  const getCountryApiValue = useCallback((countryName) => {
    const country = countryName?.toLowerCase().trim();

    switch (country) {
      case "latvia":
      case "lv":
      case "lat":
        return "latvia";
      case "lithuania":
      case "lt":
      case "ltu":
        return "lithuania";
      case "estonia":
      case "ee":
      case "est":
        return "estonia";
      default:
        return "";
    }
  }, []);

  const getStatusApiValue = useCallback((status) => {
    switch (status) {
      case "In-Process":
        return "0,1,2";
      case "AI Assessed":
        return "3";
      case "Under Review":
        return "5";
      case "Failed":
        return "6";
      case "Awaiting Retry":
        return "7";
      case "Review Completed":
        return "8";
      default:
        return "";
    }
  }, []);

  const getWarningApiValue = useCallback((warningText) => {
    if (!warningText) {
      return "";
    }

    const normalized = warningText.toLowerCase().trim();

    // If the value is already a warning code, return it directly.
    if (/^w\d{3}$/.test(normalized)) {
      return normalized.toUpperCase();
    }

    const matchedWarning = WARNING_CODES.find(
      (warning) =>
        warning.description.toLowerCase().trim() === normalized ||
        warning.code.toLowerCase().trim() === normalized,
    );

    return matchedWarning ? matchedWarning.code : "";
  }, []);

  // Function to open all warnings modal
  const openAllWarningsModal = useCallback((warnings) => {
    setAllWarningsData(warnings);
    setShowAllWarningsModal(true);
  }, []);

  // Function to close all warnings modal
  const closeAllWarningsModal = useCallback(() => {
    setShowAllWarningsModal(false);
    setAllWarningsData([]);
  }, []);

  const [error, setError] = useState(null);

  // Fetch assessment report data for the full filtered dataset.
  const fetchAssessmentReport = useCallback(
    async ({
      country = selectedCountryRef.current,
      status = selectedStatusRef.current,
      warning = selectedWarningRef.current,
      dateRange = startDate && endDate ? { startDate, endDate } : null,
    } = {}) => {
      try {
        setReportLoading(true);

        const params = {};
        const apiCountry = getCountryApiValue(country);
        if (apiCountry) {
          params.country_id = apiCountry;
          params.countryname = apiCountry;
        }

        const apiStatus = getStatusApiValue(status);
        const apiWarning = getWarningApiValue(warning);
        if (apiWarning && apiWarning !== "All") {
          params.warning_id = apiWarning;
        }

        if (dateRange?.startDate && dateRange?.endDate) {
          params.start_date = dateRange.startDate;
          params.stop_date = dateRange.endDate;
        }

        // "In-Process" is really three claim_state codes (0,1,2), but
        // /assessment_report answers the exact same total/completed/failed
        // for all three — confirmed directly: three separate calls with
        // status=0, status=1, status=2 came back with the identical
        // total_claims. Summing them (the previous approach) tripled the
        // count instead of adding it up, so this sends only the first code.
        if (apiStatus) {
          params.status = apiStatus.includes(",") ? apiStatus.split(",")[0] : apiStatus;
        }
        const response = await getAssessmentReport(params);
        const reportResponse = {
          total: parseInt(response?.total ?? response?.total_claims) || 0,
          completed: parseInt(response?.completed) || 0,
          failed: parseInt(response?.failed) || 0,
        };

        setReportData({
          totalInspections: reportResponse.total,
          completedInspections: reportResponse.completed,
          incorrectImages: reportResponse.failed,
        });
      } catch (error) {
        setReportData({
          totalInspections: 0,
          completedInspections: 0,
          incorrectImages: 0,
        });
      } finally {
        setReportLoading(false);
      }
    },
    [
      endDate,
      getCountryApiValue,
      getStatusApiValue,
      getWarningApiValue,
      startDate,
    ],
  );

  // Fetch cases data from API with pagination or infinite scroll
  const fetchCases = useCallback(
    async (
      page = 1,
      dateRange = null,
      isLoadingMore = false,
      isSilent = false,
    ) => {
      try {
        if (dateRange) {
          console.log("Date range filter:", dateRange);
        }

        // Don't show loading states during silent polling
        if (!isSilent) {
          if (isLoadingMore) {
            setIsLoadingMore(true);
          } else {
            setLoading(true);
          }
        }

        // Build API parameters
        const apiParams = {
          per_page_limit: 15, // Consistent page size
        };

        // Add pagination parameter
        if (isLoadingMore) {
          // For infinite scroll loading more, increment the page number
          currentPageRef.current = currentPageRef.current + 1;
          apiParams.page_num = currentPageRef.current;
          console.log("Loading page:", apiParams.page_num);
        } else {
          // For initial load or date filter changes, reset to page 1
          currentPageRef.current = 1;
          apiParams.page_num = 1;
          console.log("Initial/Reset load - page:", apiParams.page_num);
        }

        // Add date range parameters
        // Use provided date range, or default to 2025-06-15 to today if no dates selected
        if (dateRange && dateRange.startDate && dateRange.endDate) {
          apiParams.start_date = dateRange.startDate;
          apiParams.stop_date = dateRange.endDate; // API expects 'stop_date', not 'end_date'
          console.log("Adding custom date filters to API call:", apiParams);
        } else if (!startDate && !endDate) {
          // Fallback: default to last 30 days when no dates are set
          const defaultRange = getLast30DayRange();
          apiParams.start_date = defaultRange.startDate;
          apiParams.stop_date = defaultRange.endDate;
          console.log(
            "Using default date range:",
            apiParams.start_date,
            "to",
            apiParams.stop_date,
          );
        }

        const activeCountry = getCountryApiValue(selectedCountryRef.current);
        if (activeCountry) {
          // country_id, not countryname — confirmed against a live request:
          // the backend filters on country_id and ignores an empty countryname.
          apiParams.country_id = activeCountry;
          console.log("Adding country filter:", activeCountry);
        }

        const activeStatus = getStatusApiValue(selectedStatusRef.current);
        if (activeStatus) {
          apiParams.claim_state = activeStatus;
          console.log("Adding status filter:", activeStatus);
        }

        // Use a ref here because fetchCases is memoized and reused by polling/scroll handlers.
        const activeWarning = selectedWarningRef.current;
        if (activeWarning && activeWarning !== "All") {
          const warningApiValue = getWarningApiValue(activeWarning);
          if (warningApiValue) {
            apiParams.warning_id = warningApiValue;
            console.log("Adding warning filter:", warningApiValue);
          } else {
            console.log(
              "Selected warning does not map to a warning_id; skipping server filter:",
              activeWarning,
            );
          }
        }

        // For simplicity, let's fetch a larger dataset and implement client-side pagination
        // You can modify this to use server-side pagination if the API supports offset/page parameters
        const response = await getAssessmentList(apiParams);

        if (response && response.data) {
          console.log("Response data:", response.data);

          // Convert object to array since API returns data as object with keys
          const dataArray = Object.values(response.data).sort(
            (a, b) => new Date(b.created_at) - new Date(a.created_at),
          );
          if (dataArray[0]) {
            console.log("[CasesImg] assessment[0] keys:", Object.keys(dataArray[0]));
            console.log("[CasesImg] assessment[0].car_images:", JSON.stringify(dataArray[0].car_images));
          }

          // Transform API data to match our component structure
          const transformedCases = dataArray.map((assessment, index) => {
            const warningsData = getWarnings(assessment);
            return {
              id: assessment.id || index,
              image: getVehicleImageFromAPI(assessment), // Use API image with fallback
              status: getStatusInfoFor(parseInt(assessment.claim_state), getClientEntry(getSourceName())?.statusLabels).text,
              statusIcon: getStatusIcon(parseInt(assessment.claim_state)),
              statusColor: getStatusColor(parseInt(assessment.claim_state)),
              caseId: assessment.ref_num || assessment.id || "N/A",
              fullCaseId: assessment.ref_num || assessment.id || "N/A",
              country: assessment.country_corp_id || "Unknown",
              flag: getCountryFlag(assessment.country_corp_id),
              warnings: warningsData,
              timestamp:
                formatDateTime(assessment.created_at) ||
                assessment.created_at ||
                "N/A",
              opacity:
                parseInt(assessment.claim_state) <= 2 ? "opacity-50" : "",
              assessmentData: assessment, // Keep original data for reference
            };
          });

          if (isLoadingMore) {
            // For infinite scroll, append new data to existing cases
            setAllCases((prevCases) => [...prevCases, ...transformedCases]);
            setCases((prevCases) => [...prevCases, ...transformedCases]);
            setNewCasesIds(transformedCases.map((item) => item.id));

            // Check if we have more data (if less than 15 items returned, we've reached the end)
            if (transformedCases?.length < 5) {
              setHasMoreData(false);
            } else {
              console.log(
                "More data available, current page:",
                currentPageRef.current,
              );
            }
          } else {
            // For initial load or filter changes, replace all data
            setAllCases(transformedCases);
            setCases(transformedCases);
            setNewCasesIds([]);
            setHasMoreData(transformedCases.length >= 15);
          }
        } else {
          console.log("No data in response or response is null");
        }
      } catch (err) {
        console.error("Error fetching cases:", err);
        setError("Failed to load cases");
      } finally {
        // Don't update loading states during silent polling
        if (!isSilent) {
          if (isLoadingMore) {
            console.log(
              "✅ Resetting isLoadingMore to false - Page:",
              currentPageRef.current,
            );
            setIsLoadingMore(false);
          } else {
            setLoading(false);
          }
        }
      }
    },
    [getCountryApiValue, getStatusApiValue], // eslint-disable-line react-hooks/exhaustive-deps
  );

  // Initial load effect
  useEffect(() => {
    fetchCases(1, getLast30DayRange());
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch report data when full-dataset filters change
  useEffect(() => {
    const dateRange = startDate && endDate ? { startDate, endDate } : null;
    fetchAssessmentReport({
      country: selectedCountry,
      status: selectedStatus,
      warning: selectedWarning,
      dateRange,
    });
  }, [
    selectedCountry,
    selectedStatus,
    selectedWarning,
    startDate,
    endDate,
    fetchAssessmentReport,
  ]);

  // Polling effect for processing cases
  useEffect(() => {
    // Check if any case has an in-process status
    const hasProcessingCases = cases.some((caseItem) =>
      caseItem.status === "In-Process",
    );

    if (hasProcessingCases) {
      // Clear any existing interval
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }

      // Set up polling every 5 seconds
      pollingIntervalRef.current = setInterval(() => {
        // Refresh cases data without showing loading state (silent = true)
        fetchCases(
          1,
          startDate && endDate ? { startDate, endDate } : null,
          false,
          true,
        );
      }, 5000);
    } else {
      // No processing cases, clear the interval
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    }

    // Cleanup function
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, [cases, startDate, endDate, fetchCases]); // Dependencies: cases, date filters, and fetchCases

  // Cleanup polling on component unmount
  useEffect(() => {
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, []);

  // Infinite scroll effect
  useEffect(() => {
    if (!useInfiniteScroll) return;

    const handleScroll = () => {
      const container = scrollContainerRef.current;
      if (!container) return;

      const { scrollTop, scrollHeight, clientHeight } = container;
      const distanceFromBottom = scrollHeight - (scrollTop + clientHeight);

      // TEMPORARY hard cap — /assessment_report's total and the list
      // endpoint's own "is there another page" signal (hasMoreData, a
      // page-came-back-short heuristic) are two different numbers that can
      // disagree, and infinite scroll would otherwise keep paginating past
      // the reported total. Only trusted for v2 (Hegic) when status is a
      // generic filter the report can't apply at all — getAssessmentReport's
      // v2 branch answers with a lifetime total regardless of status, unlike
      // the legacy branch fetchAssessmentReport sends `status` to above, so
      // comparing it against a status-filtered `cases.length` would be
      // comparing two different counts. Remove this one clause once the list
      // API's own pagination is reliable on its own.
      const totalIsComparable =
        reportData.totalInspections > 0 && getApiGeneration() !== "v2";
      const belowReportedTotal =
        !totalIsComparable || cases.length < reportData.totalInspections;

      // Load more at 200px from bottom
      if (
        distanceFromBottom <= 200 &&
        !isLoadingMore &&
        !loading &&
        hasMoreData &&
        belowReportedTotal
      ) {
        console.log(
          "🚀 Triggering load more - Page:",
          currentPageRef.current + 1,
          "isLoadingMore:",
          isLoadingMore,
          "loading:",
          loading,
          "hasMoreData:",
          hasMoreData,
        );
        const dateRange = startDate && endDate ? { startDate, endDate } : null;
        setIsLoadingMore(true);
        fetchCases(1, dateRange, true, false);
      }
    };

    // Find scroll container
    const findContainer = () => {
      const container = document.getElementById("cases-list-container");
      if (container) {
        scrollContainerRef.current = container;
        container.addEventListener("scroll", handleScroll, { passive: true });
        return true;
      }
      return false;
    };

    // Try to attach listener
    if (!findContainer()) {
      // Retry with delays
      const timeouts = [50, 100, 200, 500, 1000].map((delay) =>
        setTimeout(() => !scrollContainerRef.current && findContainer(), delay),
      );

      return () => {
        timeouts.forEach(clearTimeout);
        if (scrollContainerRef.current) {
          scrollContainerRef.current.removeEventListener(
            "scroll",
            handleScroll,
          );
          scrollContainerRef.current = null;
        }
      };
    }

    return () => {
      if (scrollContainerRef.current) {
        scrollContainerRef.current.removeEventListener("scroll", handleScroll);
        scrollContainerRef.current = null;
      }
    };
  }, [
    useInfiniteScroll,
    isLoadingMore,
    loading,
    hasMoreData,
    startDate,
    endDate,
    fetchCases,
    cases,
    reportData.totalInspections,
  ]);

  // Legacy sample cases data (keeping for fallback)
  /* const sampleCases = [
    {
      id: 1,
      image: car1,
      status: "In-Process",
      statusIcon: loadingIcon,
      statusColor: "#1e2125",
      caseId: "MP6672",
      country: "Latvia",
      flag: <LvLatvia />,
      warnings: "Last refreshed 2:20PM",
      warningsOpacity: "opacity-20",
      timestamp: "2021-02-05 08:28:36",
      opacity: "opacity-50",
    },
    {
      id: 2,
      image: car2,
      status: "Review Completed",
      statusIcon: thumbsUpIcon,
      statusColor: "#36a149",
      caseId: "MP6672",
      country: "Latvia",
      flag: <LvLatvia />,
      warnings: "No warnings",
      warningsOpacity: "opacity-20",
      timestamp: "2021-02-05 08:28:36",
      opacity: "",
    },
    {
      id: 3,
      image: car3,
      status: "AI Assessed",
      statusIcon: thumbsUpIcon,
      statusColor: "#36a149",
      caseId: "MP6672",
      country: "Latvia",
      flag: <LvLatvia />,
      warnings: "No warnings",
      warningsOpacity: "opacity-20",
      timestamp: "2021-02-05 08:28:36",
      opacity: "",
    },
    {
      id: 4,
      image: car4,
      status: "Review Completed",
      statusIcon: alertTriangleIcon,
      statusColor: "#36a149",
      caseId: "MP6672",
      country: "Lithuania",
      flag: <LtLithuania />,
      warnings: "Reflection/Glare/Shadow detected",
      warningsOpacity: "",
      timestamp: "2021-02-05 08:28:36",
      opacity: "",
    },
    {
      id: 5,
      image: car1,
      status: "AI Assessed",
      statusIcon: alertTriangleIcon,
      statusColor: "#36a149",
      caseId: "MP6672",
      country: "Latvia",
      flag: <LvLatvia />,
      warnings: "VIN not readable",
      warningsOpacity: "",
      timestamp: "2021-02-05 08:28:36",
      opacity: "",
      multilineWarnings: true,
    },
    {
      id: 6,
      image: car2,
      status: "Failed",
      statusIcon: thumbsDownIcon,
      statusColor: "#df3822",
      caseId: "MP6672",
      country: "Latvia",
      flag: <LvLatvia />,
      warnings: "No warnings",
      warningsOpacity: "opacity-20",
      timestamp: "2021-02-05 08:28:36",
      opacity: "",
    },
    {
      id: 7,
      image: car5,
      status: "AI Assessed",
      statusIcon: alertTriangleIcon,
      statusColor: "#43b958",
      caseId: "MP6672",
      country: "Latvia",
      flag: <LvLatvia />,
      warnings: (
        <div className="font-['Noto_Sans:Regular',_sans-serif] font-normal leading-[0] relative shrink-0 text-text-primary text-base tracking-[-0.16px] w-full max-w-[406px]">
          <p className="leading-[normal] mb-0 text-base">VIN not readable</p>
          <p className="leading-[normal] mb-0 text-base">
            Number plate not readable
          </p>
          <p className="leading-[normal] text-base">
            <span>Incorrect image uploaded for front side • </span>
            <span
              className="text-[#0e53e8] cursor-pointer"
              onClick={() => setShowPopup(true)}
            >
              View
            </span>
          </p>
        </div>
      ),
      warningsOpacity: "",
      timestamp: "2021-02-05 08:28:36",
      timestampColor: "#4c5055",
      opacity: "",
      multilineWarnings: true,
      height: "h-auto min-h-[80px]",
    },
    {
      id: 8,
      image: car6,
      status: "Under Review",
      statusIcon: refreshIcon,
      statusColor: "#f8bc05",
      caseId: "MP6672",
      country: "Latvia",
      flag: <LvLatvia />,
      warnings: "No warnings",
      warningsOpacity: "opacity-20",
      timestamp: "2021-02-05 08:28:36",
      opacity: "",
    },
    {
      id: 9,
      image: car4,
      status: "Under Review",
      statusIcon: refreshIcon,
      statusColor: "#f8bc05",
      caseId: "MP6672",
      country: "Latvia",
      flag: <LvLatvia />,
      warnings: "No warnings",
      warningsOpacity: "opacity-20",
      timestamp: "2021-02-05 08:28:36",
      opacity: "",
    },
    {
      id: 10,
      image: car6,
      status: "Under Review",
      statusIcon: refreshIcon,
      statusColor: "#f8bc05",
      caseId: "MP6672",
      country: "Latvia",
      flag: <LvLatvia />,
      warnings: "No warnings",
      warningsOpacity: "opacity-20",
      timestamp: "2021-02-05 08:28:36",
      opacity: "",
    },
    {
      id: 11,
      image: car7,
      status: "Awaiting Retry",
      statusIcon: hourglassIcon,
      statusColor: "#ffaa76",
      caseId: "MP6672",
      country: "Lithuania",
      flag: <LtLithuania />,
      warnings: (
        <div className="flex gap-[8px] items-center">
          <div className="overflow-clip relative shrink-0 size-[24px]">
            <img
              alt="Warning"
              className="block max-w-none size-full"
              src={alertTriangleIcon}
              style={{ filter: "hue-rotate(45deg)" }}
            />
          </div>
          <div className="flex flex-col font-['Noto_Sans:Medium',_sans-serif] font-medium justify-end leading-[0] overflow-ellipsis overflow-hidden relative shrink-0 text-text-primary text-base text-nowrap tracking-[-0.32px]">
            <p className="leading-[32px] overflow-ellipsis overflow-hidden whitespace-pre">
              Reflection/Glare/Shadow detected
            </p>
          </div>
        </div>
      ),
      warningsOpacity: "",
      timestamp: "2021-02-05 08:28:36",
      opacity: "",
    },
  ]; */

  // Filter and search functionality
  const applyFilters = useCallback(() => {
    // Skip filtering if we're in search mode (search results should not be filtered)
    if (isSearchMode) {
      return;
    }

    // For infinite scroll with server-side pagination, filter the accumulated cases
    // For traditional pagination, filter all cases
    const sourceData = cases;
    let filtered = [...sourceData];

    // Apply status filter
    if (selectedStatus !== "All") {
      filtered = filtered.filter(
        (caseItem) => caseItem.status === selectedStatus,
      );
    }

    // Apply country filter
    if (selectedCountry !== "All") {
      filtered = filtered.filter(
        (caseItem) =>
          caseItem.country?.toLowerCase() === selectedCountry.toLowerCase(),
      );
    }

    // Apply warning filter
    if (selectedWarning !== "All") {
      const selectedWarningCode = getWarningApiValue(selectedWarning);
      if (!selectedWarningCode) {
        filtered = filtered.filter((caseItem) =>
          Array.isArray(caseItem.warnings) &&
          caseItem.warnings.some(
            (warning) => warning.message?.trim() === selectedWarning,
          ),
        );
      }
    }

    // Note: Date filtering is now handled by the API, not client-side
    // Note: Search is now handled by the API via handleSearch function, not client-side

    // Update pagination for filtered results
    if (useInfiniteScroll) {
      // TEMPORARY hard cap — the scroll handler only stops further fetches
      // once the known total is reached, but a single page already in
      // flight when that happens can still land a few rows past it. This
      // truncates what's actually rendered/counted to the reported total
      // too, so "Showing 1-X of Y" can never show X > Y. Same v2 exemption as
      // the scroll handler's own belowReportedTotal above — a lifetime total
      // isn't comparable to a status-filtered list. Remove once the list
      // API's own pagination is reliable on its own.
      if (
        getApiGeneration() !== "v2" &&
        reportData.totalInspections > 0 &&
        filtered.length > reportData.totalInspections
      ) {
        filtered = filtered.slice(0, reportData.totalInspections);
      }
      // For infinite scroll, show all filtered cases from the current visible cases
      setFilteredCases(filtered);
    } else {
      // For traditional pagination, apply pagination to filtered results
      const totalItems = filtered.length;
      const calculatedTotalPages = Math.ceil(totalItems / itemsPerPage);
      setTotalPages(calculatedTotalPages);

      // Reset to first page when filters change and current page is beyond available pages
      const pageToUse = currentPage > calculatedTotalPages ? 1 : currentPage;
      if (pageToUse !== currentPage && calculatedTotalPages > 0) {
        setCurrentPage(1);
        return; // Exit early, will re-run with page 1
      }

      // Get current page items from filtered results
      const startIndex = (pageToUse - 1) * itemsPerPage;
      const endIndex = startIndex + itemsPerPage;
      const currentPageItems = filtered.slice(startIndex, endIndex);

      setFilteredCases(currentPageItems);
    }
  }, [
    cases,
    selectedStatus,
    selectedCountry,
    selectedWarning,
    currentPage,
    itemsPerPage,
    useInfiniteScroll,
    isSearchMode,
    getWarningApiValue,
    reportData.totalInspections,
  ]);

  const resetFilters = useCallback(() => {
    setSelectedStatus("All");
    setSelectedCountry("All");
    setSelectedWarning("All");
    selectedStatusRef.current = "All";
    selectedCountryRef.current = "All";
    selectedWarningRef.current = "All";
    const defaultRange = getLast30DayRange();
    setStartDate(defaultRange.startDate);
    setEndDate(defaultRange.endDate);
    setSearchQuery("");
    setIsSearchMode(false);
    setShowStatusDropdown(false);
    setShowCountryDropdown(false);
    setShowWarningDropdown(false);
    setShowDatePicker(false);
    setCurrentPage(1);
    currentPageRef.current = 1;
    setHasMoreData(true);

    if (useInfiniteScroll) {
      setCases([]);
      setAllCases([]);
      setFilteredCases([]);
    }

    fetchCases(1, defaultRange);
  }, [fetchCases, useInfiniteScroll]);

  // Apply filters whenever dependencies change
  useEffect(() => {
    applyFilters();
  }, [applyFilters]);

  // Get unique values for dropdown options
  const getUniqueStatuses = useCallback(() => {
    return filtersConfig.status?.options || ["All"];
  }, []);

  // Helper function to get status code from status text for icon mapping
  const getStatusCodeFromText = useCallback((statusText) => {
    switch (statusText) {
      case "In-Process":
        return 0;
      case "AI Assessed":
        return 3;
      case "Under Review":
        return 5;
      case "Review Completed":
        return 8;
      case "Failed":
        return 6;
      default:
        return 0;
    }
  }, []);

  const getUniqueCountries = useCallback(() => {
    return filtersConfig.country?.options || ["All"];
  }, []);

  const getUniqueWarnings = useCallback(() => {
    return WARNING_CODES.map((warning) =>
      warning.code === "All" ? "All" : warning.description,
    );
  }, []);

  // Dropdown toggle handlers
  const toggleStatusDropdown = () => {
    setShowStatusDropdown(!showStatusDropdown);
    setShowCountryDropdown(false);
    setShowWarningDropdown(false);
    setShowDatePicker(false);
  };

  const toggleCountryDropdown = () => {
    setShowCountryDropdown(!showCountryDropdown);
    setShowStatusDropdown(false);
    setShowWarningDropdown(false);
    setShowDatePicker(false);
  };

  const toggleWarningDropdown = () => {
    setShowWarningDropdown(!showWarningDropdown);
    setShowStatusDropdown(false);
    setShowCountryDropdown(false);
    setShowDatePicker(false);
  };

  const toggleDatePicker = () => {
    setShowDatePicker(!showDatePicker);
    setShowStatusDropdown(false);
    setShowCountryDropdown(false);
    setShowWarningDropdown(false);
  };

  // Filter selection handlers
  const handleStatusSelect = (status) => {
    setSelectedStatus(status);
    selectedStatusRef.current = status;
    setShowStatusDropdown(false);
    setCurrentPage(1);
    currentPageRef.current = 1;
    setHasMoreData(true);

    if (useInfiniteScroll) {
      setCases([]);
      setAllCases([]);
    }

    const dateRange = startDate && endDate ? { startDate, endDate } : null;
    fetchCases(1, dateRange, false, false);
  };

  const handleCountrySelect = (country) => {
    setSelectedCountry(country);
    selectedCountryRef.current = country;
    setShowCountryDropdown(false);

    setCurrentPage(1);
    currentPageRef.current = 1;
    setHasMoreData(true);

    if (useInfiniteScroll) {
      setCases([]);
      setAllCases([]);
    }

    const dateRange = startDate && endDate ? { startDate, endDate } : null;
    fetchCases(1, dateRange, false, false);
  };

  const handleWarningSelect = (warningCode) => {
    setSelectedWarning(warningCode);
    selectedWarningRef.current = warningCode;
    setShowWarningDropdown(false);
    setCurrentPage(1);

    currentPageRef.current = 1;
    setHasMoreData(true);

    if (useInfiniteScroll) {
      setCases([]);
      setAllCases([]);
    }

    const dateRange = startDate && endDate ? { startDate, endDate } : null;
    fetchCases(1, dateRange, false, false);
  };

  const handleDateRangeSelect = () => {
    setShowDatePicker(false);
    setCurrentPage(1);

    if (useInfiniteScroll) {
      currentPageRef.current = 1;
      setHasMoreData(true);
      setCases([]);
      setAllCases([]);
    }

    const dateRange = startDate && endDate ? { startDate, endDate } : null;
    fetchCases(1, dateRange);
  };

  const clearDateRange = () => {
    const defaultRange = getLast30DayRange();
    setStartDate(defaultRange.startDate);
    setEndDate(defaultRange.endDate);
    setShowDatePicker(false);
    setCurrentPage(1);

    if (useInfiniteScroll) {
      currentPageRef.current = 1;
      setHasMoreData(true);
      setCases([]);
      setAllCases([]);
    }

    fetchCases(1, defaultRange);
  };

  const setDateShortcut = (days) => {
    const today = new Date();
    const pastDate = new Date();
    pastDate.setDate(today.getDate() - days);

    const formatDate = (date) => date.toISOString().split("T")[0];

    setStartDate(formatDate(pastDate));
    setEndDate(formatDate(today));
  };

  // Search by ref_num using the dedicated search API
  const handleSearch = async (query) => {
    const trimmedQuery = query.trim();

    if (!trimmedQuery) {
      setIsSearchMode(false);
      setError(null);
      const dateRange = startDate && endDate ? { startDate, endDate } : null;
      fetchCases(1, dateRange, false, false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setIsSearchMode(true);
      setCurrentPage(1);
      currentPageRef.current = 1;
      setHasMoreData(false);

      const searchResults = await searchCases({
        ref_num: trimmedQuery,
        source_name: clientConfig.sourceName,
        country_id: "",
        countryname: "",
      });

      let dataArray = [];

      if (searchResults) {
        if (Array.isArray(searchResults)) {
          dataArray = searchResults;
        } else if (searchResults.data && Array.isArray(searchResults.data)) {
          dataArray = searchResults.data;
        } else if (typeof searchResults === "object") {
          dataArray = Object.values(searchResults);
        }
      }

      if (dataArray.length > 0) {
        const transformedSearchResults = dataArray.map((assessment, index) => {
          const warningsData = getWarnings(assessment);
          return {
            id: assessment.id || index,
            image: getVehicleImageFromAPI(assessment),
            status: getStatusInfoFor(parseInt(assessment.claim_state), getClientEntry(getSourceName())?.statusLabels).text,
            statusIcon: getStatusIcon(parseInt(assessment.claim_state)),
            statusColor: getStatusColor(parseInt(assessment.claim_state)),
            caseId: formatDisplayId(assessment),
            fullCaseId: assessment.ref_num || assessment.id || "N/A",
            country: assessment.country_corp_id || "Unknown",
            flag: getCountryFlag(assessment.country_corp_id),
            warnings: warningsData,
            timestamp:
              formatDateTime(assessment.created_at) ||
              assessment.created_at ||
              "N/A",
            opacity: parseInt(assessment.claim_state) <= 2 ? "opacity-50" : "",
            assessmentData: assessment,
          };
        });

        // /search takes no date params at all, so it answers across every
        // date regardless of what's picked. A search is still a filter on
        // this page; it shouldn't be the one way to see a case the date
        // range is meant to exclude.
        const dateRange = startDate && endDate ? { startDate, endDate } : null;
        const dateFilteredResults = dateRange
          ? transformedSearchResults.filter((row) =>
              withinListRange(row.assessmentData?.created_at, dateRange),
            )
          : transformedSearchResults;

        setAllCases(dateFilteredResults);
        setCases(dateFilteredResults);
        setFilteredCases(dateFilteredResults);
        setTotalPages(dateFilteredResults.length > 0 ? 1 : 0);
      } else {
        setAllCases([]);
        setCases([]);
        setFilteredCases([]);
        setTotalPages(0);
      }
    } catch (searchError) {
      console.error("Search error:", searchError);
      setError("Failed to search cases");
    } finally {
      setLoading(false);
    }
  };

  // Pagination handlers
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handleCaseClick = (caseItem) => {
    // Disable click for processing status
    if (caseItem.status === "In-Process") {
      return;
    }

    // Use the ref_num from the API data for navigation
    const refNum =
      caseItem.assessmentData?.ref_num ||
      caseItem.fullCaseId ||
      caseItem.caseId;
    // The detail endpoint's own response has no country field at all
    // (confirmed against a real /assessment_new payload — only a numeric
    // country_id, no corp_id/name) — carry the list's own value along so
    // the detail header still has somewhere to read it from.
    window.open(
      withCountryParam(`/cases/${encodeRefNum(refNum)}`, caseItem.country),
      "_blank",
    );
  };

  // Show error state
  if (error) {
    return (
      <div className="bg-background-primary relative min-h-screen w-full">
        <div className="flex items-center justify-center h-screen">
          <div className="text-center">
            <p className="text-red-600 mb-4">{error}</p>
            <button
              onClick={() => window.location.reload()}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }



  return (
    <div className="bg-background-primary relative h-full min-h-0 w-full overflow-hidden">
      <OnboardingTour
        tourKey="cases"
        steps={TOUR_STEPS.cases}
        ready={!loading && filteredCases.length > 0}
      />
      {/* Main Content */}
      <div className={`flex h-full min-h-0 flex-col px-4 lg:px-6 xl:px-8 ${useInfiniteScroll ? "pb-2" : "pb-6 lg:pb-8"}`}>
        {/* Statistics Container */}
        <div className="pt-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Total Inspections */}
            <div className="bg-white rounded-[12px] border border-border-light p-6 shadow-sm">
              <div className="flex flex-col">
                <div className="text-[#6b7280] text-sm font-medium mb-2">
                  Total Inspections
                </div>
                <div className="text-[#1f2937] text-[32px] font-bold leading-[1]">
                  {reportLoading
                    ? "..."
                    : reportData.totalInspections.toLocaleString()}
                </div>
              </div>
            </div>

            {/* Completed Inspections */}
            <div className="bg-white rounded-[12px] border border-border-light p-6 shadow-sm">
              <div className="flex flex-col">
                <div className="text-[#6b7280] text-sm font-medium mb-2">
                  Completed Inspections
                </div>
                <div className="text-[#059669] text-[32px] font-bold leading-[1]">
                  {reportLoading
                    ? "..."
                    : reportData.completedInspections.toLocaleString()}
                </div>
              </div>
            </div>

            {/* Inspections with Incorrect Images */}
            <div className="bg-white rounded-[12px] border border-border-light p-6 shadow-sm">
              <div className="flex flex-col">
                <div className="text-[#6b7280] text-sm font-medium mb-2">
                  Inspections with Incorrect Images
                </div>
                <div className="text-[#dc2626] text-[32px] font-bold leading-[1]">
                  {reportLoading
                    ? "..."
                    : reportData.incorrectImages.toLocaleString()}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Filters Bar */}
        <div className="flex flex-col lg:flex-row lg:justify-between items-start lg:items-center gap-4 mb-6">
          <div className="flex flex-wrap gap-3">
            {/* All Cases Button */}
            <button
              className="bg-primary box-border flex gap-[8px] items-center justify-center overflow-clip px-[16px] py-[10px] rounded-[8px]"
              onClick={resetFilters}
            >
              <div
                className="font-['Noto_Sans:Display_SemiBold',_sans-serif] font-semibold leading-[0] text-sm text-nowrap text-white"
                style={{ fontVariationSettings: "'CTGR' 100, 'wdth' 100" }}
              >
                <p className="leading-[20px] whitespace-pre">All Cases</p>
              </div>
            </button>

            {/* Status Dropdown */}
            {filtersConfig.status?.enabled !== false && <div
              ref={statusDropdownRef}
              data-tour="cases-status-filter"
              className="bg-background-primary relative rounded-[8px] border border-border-primary"
            >
              <div
                className="box-border flex gap-[8px] items-center justify-center overflow-clip pl-[16px] pr-[8px] py-[10px] cursor-pointer"
                onClick={toggleStatusDropdown}
              >
                <div
                  className="font-['Noto_Sans:Display_SemiBold',_sans-serif] font-semibold leading-[0] text-text-primary text-sm text-nowrap"
                  style={{ fontVariationSettings: "'CTGR' 100, 'wdth' 100" }}
                >
                  <p className="leading-[20px] whitespace-pre">
                    {selectedStatus === "All" ? "Status" : selectedStatus}
                  </p>
                </div>
                <div className="relative size-[20px] shrink-0">
                  <div className="absolute inset-[44.79%_36.46%_40.63%_36.46%]">
                    <div className="absolute inset-[-21.43%_-11.54%]">
                      <img
                        alt="Chevron down"
                        className="block max-w-none size-full"
                        src={chevronDownFigma}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Status Dropdown Menu */}
              {showStatusDropdown && (
                <div className="absolute top-full left-0 bg-white border border-border-primary rounded-[8px] shadow-lg z-10 mt-1 min-w-[220px] py-2">
                  {getUniqueStatuses().map((status) => (
                    <div
                      key={status}
                      className="px-6 py-3 hover:bg-gray-100 cursor-pointer text-sm font-medium flex items-center gap-3"
                      onClick={() => handleStatusSelect(status)}
                    >
                      {status !== "All" && (
                        <div className="overflow-clip relative shrink-0 size-[20px]">
                          <img
                            alt={status}
                            className="block max-w-none size-full"
                            src={getStatusInfo(getStatusCodeFromText(status)).icon}
                          />
                        </div>
                      )}
                      {status === "All" && (
                        <div className="size-[20px]"></div> // Spacer for alignment
                      )}
                      <span>{status}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            }

            {/* Country Dropdown */}
            {showCountryFilter && <div
              ref={countryDropdownRef}
              className="bg-background-primary relative rounded-[8px] border border-border-primary"
            >
              <div
                className="box-border flex gap-[8px] items-center justify-center overflow-clip pl-[16px] pr-[8px] py-[10px] cursor-pointer"
                onClick={toggleCountryDropdown}
              >
                <div
                  className="font-['Noto_Sans:Display_SemiBold',_sans-serif] font-semibold leading-[0] text-text-primary text-sm text-nowrap"
                  style={{ fontVariationSettings: "'CTGR' 100, 'wdth' 100" }}
                >
                  <p className="leading-[20px] whitespace-pre">
                    {selectedCountry === "All" ? "Country" : selectedCountry}
                  </p>
                </div>
                <div className="relative size-[20px] shrink-0">
                  <div className="absolute inset-[44.79%_36.46%_40.63%_36.46%]">
                    <div className="absolute inset-[-21.43%_-11.54%]">
                      <img
                        alt="Chevron down"
                        className="block max-w-none size-full"
                        src={chevronDownFigma}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Country Dropdown Menu */}
              {showCountryDropdown && (
                <div className="absolute top-full  bg-white border border-border-primary rounded-[8px] shadow-lg z-10 mt-1">
                  {getUniqueCountries().map((country) => (
                    <div
                      key={country}
                      className="px-4 py-2 hover:bg-gray-100 cursor-pointer text-sm flex items-center gap-2"
                      onClick={() => handleCountrySelect(country)}
                    >
                      {country !== "All" && (
                        <div className="w-4 h-4 flex-shrink-0">
                          {getCountryFlag(country)}
                        </div>
                      )}
                      <span>{country}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            }

            {/* Warnings Dropdown */}
            {showWarningsFilter && <div
              ref={warningDropdownRef}
              data-tour="cases-warning-filter"
              className="bg-background-primary relative rounded-[8px] border border-border-primary"
            >
              <div
                className="box-border flex gap-[8px] items-center justify-center overflow-clip pl-[16px] pr-[8px] py-[10px] cursor-pointer"
                onClick={toggleWarningDropdown}
              >
                <div
                  className="font-['Noto_Sans:Display_SemiBold',_sans-serif] font-semibold leading-[0] text-text-primary text-sm text-nowrap"
                  style={{ fontVariationSettings: "'CTGR' 100, 'wdth' 100" }}
                >
                  <p className="leading-[20px] whitespace-pre">
                    {selectedWarning === "All"
                      ? "Warnings"
                      : selectedWarning.length > 30
                        ? `${selectedWarning.substring(0, 30)}...`
                        : selectedWarning}
                  </p>
                </div>
                <div className="relative size-[20px] shrink-0">
                  <div className="absolute inset-[44.79%_36.46%_40.63%_36.46%]">
                    <div className="absolute inset-[-21.43%_-11.54%]">
                      <img
                        alt="Chevron down"
                        className="block max-w-none size-full"
                        src={chevronDownFigma}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Warnings Dropdown Menu */}
              {showWarningDropdown && (
                <div className="absolute top-full left-0 bg-white border border-border-primary rounded-[8px] shadow-lg z-10 mt-1 max-h-[400px] overflow-y-auto p-2 min-w-[220px]">
                  {getUniqueWarnings().map((warning) => (
                    <div
                      key={warning}
                      className="px-4 py-2 hover:bg-gray-100 cursor-pointer text-sm"
                      onClick={() => handleWarningSelect(warning)}
                    >
                      {warning}
                    </div>
                  ))}
                </div>
              )}
            </div>}

            {/* Date Picker */}
            {filtersConfig.dateRange?.enabled !== false && <div
              ref={datePickerRef}
              data-tour="cases-date-filter"
              className="bg-background-primary relative rounded-[8px] border border-border-primary"
            >
              <div
                className="box-border flex gap-[8px] items-center justify-center overflow-clip pl-[16px] pr-[8px] py-[10px] cursor-pointer"
                onClick={toggleDatePicker}
              >
                <div
                  className="font-['Noto_Sans:Display_SemiBold',_sans-serif] font-semibold leading-[0] text-text-primary text-sm text-nowrap"
                  style={{ fontVariationSettings: "'CTGR' 100, 'wdth' 100" }}
                >
                  <p className="leading-[20px] whitespace-pre">
                    {startDate && endDate
                      ? `${startDate} - ${endDate}`
                      : "Select Date Range"}
                  </p>
                </div>
                <div className="relative size-[20px] shrink-0">
                  <div className="absolute inset-[44.79%_36.46%_40.63%_36.46%]">
                    <div className="absolute inset-[-21.43%_-11.54%]">
                      <img
                        alt="Chevron down"
                        className="block max-w-none size-full"
                        src={chevronDownFigma}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Date Picker Panel */}
              {showDatePicker && (
                <div className="absolute top-full left-0 bg-white border border-border-primary rounded-[8px] shadow-lg z-10 mt-1 p-4 min-w-[360px]">
                  <div className="mb-4">
                    <h3 className="text-sm font-semibold text-text-primary mb-3">
                      Select Date Range
                    </h3>

                    {/* Date Shortcuts */}
                    <div className="mb-4">
                      <label className="block text-xs text-text-secondary mb-2">
                        Quick Select
                      </label>
                      <div className="flex gap-2 mb-3">
                        <button
                          onClick={() => setDateShortcut(1)}
                          className="flex-1 bg-gray-100 text-text-primary px-2 py-1 rounded text-[11px] font-medium hover:bg-gray-200 transition-colors border border-border-primary"
                        >
                          Last Day
                        </button>
                        <button
                          onClick={() => setDateShortcut(7)}
                          className="flex-1 bg-gray-100 text-text-primary px-2 py-1 rounded text-[11px] font-medium hover:bg-gray-200 transition-colors border border-border-primary"
                        >
                          Last 7 Days
                        </button>
                        <button
                          onClick={() => setDateShortcut(30)}
                          className="flex-1 bg-gray-100 text-text-primary px-2 py-1 rounded text-[11px] font-medium hover:bg-gray-200 transition-colors border border-border-primary"
                        >
                          Last 30 Days
                        </button>
                      </div>
                    </div>

                    {/* Start Date */}
                    <div className="mb-3">
                      <label className="block text-xs text-text-secondary mb-1">
                        From Date
                      </label>
                      <input
                        type="date"
                        value={startDate}
                        // A claim can't have been received tomorrow.
                        max={new Date().toISOString().split("T")[0]}
                        onChange={(e) => setStartDate(e.target.value)}
                        className="w-full px-3 py-2 border border-border-primary rounded text-sm focus:outline-none focus:border-primary"
                      />
                    </div>

                    {/* End Date */}
                    <div className="mb-4">
                      <label className="block text-xs text-text-secondary mb-1">
                        To Date
                      </label>
                      <input
                        type="date"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                        min={startDate}
                        max={new Date().toISOString().split("T")[0]}
                        className="w-full px-3 py-2 border border-border-primary rounded text-sm focus:outline-none focus:border-primary"
                      />
                    </div>

                    {/* Action Buttons */}
                    <div className="flex gap-2">
                      <button
                        onClick={handleDateRangeSelect}
                        className="flex-1 bg-primary text-white px-3 py-2 rounded text-xs font-semibold hover:bg-[#2684d1] transition-colors"
                      >
                        Apply
                      </button>
                      <button
                        onClick={clearDateRange}
                        className="flex-1 bg-gray-200 text-text-primary px-3 py-2 rounded text-xs font-semibold hover:bg-gray-300 transition-colors"
                      >
                        Clear
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>}
          </div>

          {/* Search and Inspection Link */}
          <div className="flex flex-wrap gap-3">
            {/* Search */}
            <div
              data-tour="cases-search"
              className="bg-background-primary relative rounded-[8px] border border-border-primary w-full sm:w-[350px]"
            >
              <div className="box-border flex gap-[8px] items-center overflow-clip px-[16px] py-[10px]">
                <div className="overflow-clip relative shrink-0 size-[20px]">
                  <img
                    alt="Search"
                    className="block max-w-none size-full"
                    src={searchIcon}
                  />
                </div>
                <input
                  type="text"
                  placeholder="Search any case"
                  value={searchQuery}
                  onChange={(e) => {
                    const value = e.target.value;
                    setSearchQuery(value);
                    // Only clear search results if input is completely cleared
                    if (value.trim() === "") {
                      handleSearch("");
                    }
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      handleSearch(searchQuery);
                    }
                  }}
                  className="bg-transparent flex-1 outline-none font-['Noto_Sans:Display_SemiBold',_sans-serif] font-semibold text-text-primary text-sm placeholder-[#7e848c] placeholder-opacity-50"
                  style={{
                    fontVariationSettings: "'CTGR' 100, 'wdth' 100",
                  }}
                />
                {searchQuery && (
                  <button
                    onClick={() => {
                      setSearchQuery("");
                      handleSearch("");
                    }}
                    className="text-text-secondary hover:text-text-primary px-1"
                    title="Clear search"
                  >
                    ×
                  </button>
                )}
                <button
                  onClick={() => {
                    handleSearch(searchQuery);
                  }}
                  className="bg-primary text-white px-3 py-1 rounded text-xs font-semibold hover:bg-[#2684d1] transition-colors ml-2"
                >
                  Search
                </button>
              </div>
            </div>

            {/* Inspection Link - Hidden */}
            <div className="bg-background-primary relative rounded-[8px] border border-border-primary hidden">
              <div className="box-border flex gap-[8px] items-center justify-center overflow-clip px-[16px] py-[10px]">
                <div
                  className="font-['Noto_Sans:Display_SemiBold',_sans-serif] font-semibold leading-[0] text-text-primary text-sm text-nowrap"
                  style={{
                    fontVariationSettings: "'CTGR' 100, 'wdth' 100",
                  }}
                >
                  <p className="leading-[20px] whitespace-pre">
                    Inspection Link
                  </p>
                </div>
                <div className="overflow-clip relative shrink-0 size-[20px]">
                  <img
                    alt="Link"
                    className="block max-w-none size-full"
                    src={linkIcon}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="w-full flex-1 min-h-0 overflow-x-auto">
          <div className="flex h-full min-h-0 flex-col min-w-[800px] lg:min-w-[1000px] xl:min-w-[1200px]">
            {/* Table Header */}
            <div className="flex items-center mb-4 pb-2">
              {tableColumns.vehicle?.enabled !== false && (
                <div
                  className="flex flex-col font-['Noto_Sans:Medium',_sans-serif] font-medium justify-end leading-[0] text-text-secondary text-xs tracking-[-0.12px] w-[150px] text-center"
                  style={{ fontVariationSettings: "'CTGR' 0, 'wdth' 100" }}
                >
                  <p className="leading-[normal]">{columnLabel("vehicle", "VEHICLE")}</p>
                </div>
              )}
              {tableColumns.caseId?.enabled !== false && (
                <div
                  className="flex flex-col font-['Noto_Sans:Medium',_sans-serif] font-medium justify-end leading-[0] text-text-secondary text-xs tracking-[-0.12px] flex-1 min-w-[150px] px-3 text-center"
                  style={{ fontVariationSettings: "'CTGR' 0, 'wdth' 100" }}
                >
                  <p className="leading-[normal]">{columnLabel("caseId", "CASE ID")}</p>
                </div>
              )}
              {showCountryColumn && (
                <div
                  className="flex flex-col font-['Noto_Sans:Medium',_sans-serif] font-medium justify-end leading-[0] text-text-secondary text-xs tracking-[-0.12px] flex-1 min-w-[100px] px-3 text-center"
                  style={{ fontVariationSettings: "'CTGR' 0, 'wdth' 100" }}
                >
                  <p className="leading-[normal]">{tableColumns.country?.label || "COUNTRY"}</p>
                </div>
              )}
              {showWarningsColumn && (
                <div
                  className="flex flex-col font-['Noto_Sans:Medium',_sans-serif] font-medium justify-end leading-[0] text-text-secondary text-xs tracking-[-0.12px] flex-[2] min-w-[150px] px-3 text-center"
                  style={{ fontVariationSettings: "'CTGR' 0, 'wdth' 100" }}
                >
                  <p className="leading-[normal]">{tableColumns.warnings?.label || "WARNINGS"}</p>
                </div>
              )}
              {tableColumns.timestamp?.enabled !== false && (
                <div className="w-[180px] shrink-0 justify-center px-3 flex">
                  <div
                    className="flex flex-col font-['Noto_Sans:Medium',_sans-serif] font-medium justify-center leading-[0] text-text-secondary text-xs text-nowrap tracking-[-0.12px]"
                    style={{ fontVariationSettings: "'CTGR' 0, 'wdth' 100" }}
                  >
                    <p className="leading-[normal] whitespace-pre">{tableColumns.timestamp?.label || "TIMESTAMP"}</p>
                  </div>
                </div>
              )}
              {tableColumns.status?.enabled !== false && (
                <div
                  className="flex flex-col font-['Noto_Sans:Medium',_sans-serif] font-medium justify-end leading-[0] text-text-secondary text-xs tracking-[-0.12px] flex-1 min-w-[120px] px-5 text-center"
                  style={{ fontVariationSettings: "'CTGR' 0, 'wdth' 100" }}
                >
                  <p className="leading-[normal]">{tableColumns.status?.label || "STATUS"}</p>
                </div>
              )}
            </div>

            {/* Table Rows */}
            {loading ? (
              <div className="flex items-center justify-center py-20">
                <div className="text-center">
                  <img
                    src={loadingIcon}
                    alt="Loading"
                    className="w-8 h-8 animate-spin mx-auto mb-4"
                  />
                  <p className="text-gray-600">Loading cases...</p>
                </div>
              </div>
            ) : (
              <div
                id="cases-list-container"
                className="flex flex-1 min-h-0 flex-col gap-1 overflow-auto"
              >
                {filteredCases.length > 0 ? (
                  filteredCases.map((caseItem, caseIndex) => {
                    const isProcessing = caseItem.status === "In-Process";
                    return (
                      <div
                        key={caseItem.id}
                        data-tour={caseIndex === 0 ? "cases-first-row" : undefined}
                        className={`bg-background-secondary box-border flex items-center overflow-visible rounded-[8px] w-full transition-shadow pl-0 pr-[16px] py-0 ${caseItem.opacity} relative ${isProcessing
                          ? "opacity-50 cursor-default"
                          : "cursor-pointer hover:shadow-md"
                          } ${newCasesIds.includes(caseItem.id) ? "animate-fade-in-up" : ""}`}
                        style={{
                          minHeight:
                            Array.isArray(caseItem.warnings) &&
                              caseItem.warnings?.length > 1
                              ? "100px"
                              : "80px",
                        }}
                        onClick={() => handleCaseClick(caseItem)}
                      >
                        {/* Vehicle */}
                        {tableColumns.vehicle?.enabled !== false && (
                          <div className="w-[150px] h-[80px] shrink-0 rounded-bl-[8px] rounded-tl-[8px] overflow-hidden">
                            {/* Hegic's list carries no image field at all, so
                                its rows fetch their own — see CaseThumb. */}
                            {selfFetchesThumbnail ? (
                              <CaseThumb
                                refNum={
                                  caseItem.assessmentData?.ref_num ||
                                  caseItem.fullCaseId ||
                                  caseItem.caseId
                                }
                                alt="Vehicle thumbnail"
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <img
                                alt="Vehicle thumbnail"
                                className="w-full h-full object-cover"
                                src={caseItem.image}
                              />
                            )}
                          </div>
                        )}

                        {/* Case ID */}
                        {tableColumns.caseId?.enabled !== false && (
                          <div className="flex items-center justify-center px-3 flex-1 min-w-[150px]">
                            <div
                              className="font-['Noto_Sans:Regular',_sans-serif] font-normal text-sm text-nowrap tracking-[-0.14px]"
                              style={{
                                fontVariationSettings: "'CTGR' 0, 'wdth' 100",
                                color: "#1c1c1e",
                              }}
                            >
                              <p className="leading-[normal] whitespace-pre">
                                {caseItem.caseId}
                              </p>
                            </div>
                          </div>
                        )}

                        {/* Country */}
                        {showCountryColumn && (
                          <div className="flex items-center justify-center px-3 flex-1 min-w-[100px]">
                            <div className="flex gap-[8px] items-center">
                              <div className="bg-white h-[15px] overflow-clip w-[20px]">
                                {caseItem.flag}
                              </div>
                              <div
                                className="font-['Noto_Sans:Medium',_sans-serif] font-medium text-text-primary text-base tracking-[-0.32px] w-[100px]"
                                style={{
                                  fontVariationSettings: "'CTGR' 0, 'wdth' 100",
                                }}
                              >
                                <p className="leading-[32px] overflow-ellipsis overflow-hidden">
                                  {caseItem.country}
                                </p>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Warnings */}
                        {showWarningsColumn && <div className="flex items-start px-3 flex-[2] min-w-[150px]">
                          <div className="flex flex-col gap-1 w-full">
                            {Array.isArray(caseItem.warnings) &&
                              caseItem.warnings.length > 0 ? (
                              <>
                                {(() => {
                                  const visibleWarnings =
                                    caseItem.warnings.slice(0, 2);

                                  return (
                                    <>
                                      {visibleWarnings.map(
                                        (warning, warningIndex) => (
                                          <div
                                            key={warningIndex}
                                            className="font-['Noto_Sans:Regular',_sans-serif] font-normal text-text-primary text-base tracking-[-0.16px]"
                                            style={{
                                              fontVariationSettings:
                                                "'CTGR' 0, 'wdth' 100",
                                            }}
                                          >
                                            <p className="leading-[normal] mb-0 max-w-[650px] whitespace-normal break-words">
                                              {warningIndex + 1}.{" "}
                                              {warning.message}
                                            </p>
                                          </div>
                                        ),
                                      )}

                                      {caseItem.warnings.length >
                                        visibleWarnings.length && (
                                          <div className="mt-1">
                                            <button
                                              className="bg-primary text-white px-3 py-1 rounded-[4px] text-xs font-semibold hover:bg-[#2680c7] transition-colors"
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                openAllWarningsModal(
                                                  caseItem.warnings,
                                                );
                                              }}
                                            >
                                              View More
                                            </button>
                                          </div>
                                        )}
                                    </>
                                  );
                                })()}
                              </>
                            ) : (
                              typeof caseItem.warnings === "string" &&
                              caseItem.warnings.trim() &&
                              caseItem.warnings !== "No warnings" && (
                                <div
                                  className="font-['Noto_Sans:Regular',_sans-serif] font-normal text-text-primary text-base tracking-[-0.16px]"
                                  style={{
                                    fontVariationSettings:
                                      "'CTGR' 0, 'wdth' 100",
                                  }}
                                >
                                  <p className="leading-[normal] mb-0 max-w-[700px] whitespace-normal break-words">
                                    {caseItem.warnings}
                                  </p>
                                </div>
                              )
                            )}
                          </div>
                        </div>}

                        {/* Timestamp */}
                        {tableColumns.timestamp?.enabled !== false && (
                          <div className="flex items-center justify-center px-3 w-[180px] shrink-0">
                            <div
                              className="font-['Noto_Sans:Regular',_sans-serif] font-normal text-sm text-nowrap tracking-[-0.14px]"
                              style={{
                                fontVariationSettings: "'CTGR' 0, 'wdth' 100",
                                color: "#1c1c1e",
                              }}
                            >
                              <p className="leading-[normal] whitespace-pre">
                                {caseItem.timestamp}
                              </p>
                            </div>
                          </div>
                        )}

                        {/* Status */}
                        {tableColumns.status?.enabled !== false && (
                          <div className="flex items-center justify-center px-5 flex-1 min-w-[120px] self-stretch border-l border-background-primary">
                            <div className="flex gap-[8px] items-center w-[170px]">
                              <div className="overflow-clip relative shrink-0 size-[24px]">
                                <img
                                  alt={caseItem.status}
                                  className="block max-w-none size-full"
                                  src={caseItem.statusIcon}
                                />
                              </div>
                              <div
                                className="font-['Noto_Sans:Medium',_sans-serif] font-medium leading-[0] text-sm text-nowrap tracking-[-0.28px]"
                                style={{
                                  fontVariationSettings: "'CTGR' 0, 'wdth' 100",
                                  color: caseItem.statusColor,
                                }}
                              >
                                <p className="leading-[20px] whitespace-pre">
                                  {caseItem.status}
                                </p>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })
                ) : (
                  <div className="flex flex-col items-center justify-center flex-1 h-full py-20 bg-white rounded-[8px]">
                    <div className="text-center">
                      <p className="text-text-primary font-semibold text-lg mb-2">
                        No cases found.
                      </p>
                      <p className="text-text-secondary text-sm px-6">
                        {isSearchMode 
                          ? "Please check your search criteria and try again."
                          : "We couldn't find any cases matching your current filters or search."}
                      </p>
                      <button
                        onClick={resetFilters}
                        className="mt-4 bg-primary text-white px-8 py-3 rounded-[8px] text-base font-bold hover:bg-[#2680c7] transition-colors shadow-sm"
                      >
                        {isSearchMode ? "View All Cases" : "Reset all filters"}
                      </button>
                    </div>
                  </div>
                )}

                {/* Loading indicator for infinite scroll */}
                {useInfiniteScroll && isLoadingMore && (
                  <div className="flex flex-col items-center justify-center py-8">
                    <svg
                      className="w-8 h-8 animate-spin text-[#0066cc] mb-2"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    <p className="text-text-secondary text-sm">
                      Loading more cases...
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Pagination - Only show when not using infinite scroll */}
        {!useInfiniteScroll && (
          <>
            <div className="flex justify-center items-center gap-4 mt-8 mb-4">
              {/* Previous Button */}
              <button
                onClick={handlePrevPage}
                disabled={currentPage === 1}
                className={`flex items-center gap-2 px-4 py-2 rounded-[8px] border transition-colors ${currentPage === 1
                  ? "bg-background-primary border-border-primary text-text-secondary cursor-not-allowed"
                  : "bg-white border-border-primary text-text-primary hover:bg-[#f5f5f5]"
                  }`}
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M10 12L6 8L10 4"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <span className="font-['Noto_Sans:Display_SemiBold',_sans-serif] font-semibold text-sm">
                  Previous
                </span>
              </button>

              {/* Page Numbers */}
              <div className="flex items-center gap-2">
                {/* Show page numbers with ellipsis for large page counts */}
                {(() => {
                  const pageNumbers = [];
                  const maxVisiblePages = 5;

                  if (totalPages <= maxVisiblePages) {
                    // Show all pages if total is small
                    for (let i = 1; i <= totalPages; i++) {
                      pageNumbers.push(i);
                    }
                  } else {
                    // Show pages with ellipsis
                    if (currentPage <= 3) {
                      pageNumbers.push(1, 2, 3, 4, "...", totalPages);
                    } else if (currentPage >= totalPages - 2) {
                      pageNumbers.push(
                        1,
                        "...",
                        totalPages - 3,
                        totalPages - 2,
                        totalPages - 1,
                        totalPages,
                      );
                    } else {
                      pageNumbers.push(
                        1,
                        "...",
                        currentPage - 1,
                        currentPage,
                        currentPage + 1,
                        "...",
                        totalPages,
                      );
                    }
                  }

                  return pageNumbers.map((page, index) => {
                    if (page === "...") {
                      return (
                        <span
                          key={`ellipsis-${index}`}
                          className="px-2 text-text-secondary"
                        >
                          ...
                        </span>
                      );
                    }

                    return (
                      <button
                        key={page}
                        onClick={() => handlePageChange(page)}
                        className={`w-10 h-10 rounded-[8px] font-['Noto_Sans:Display_SemiBold',_sans-serif] font-semibold text-sm transition-colors ${currentPage === page
                          ? "bg-primary text-white"
                          : "bg-white border border-border-primary text-text-primary hover:bg-[#f5f5f5]"
                          }`}
                      >
                        {page}
                      </button>
                    );
                  });
                })()}
              </div>

              {/* Next Button */}
              <button
                onClick={handleNextPage}
                disabled={currentPage === totalPages}
                className={`flex items-center gap-2 px-4 py-2 rounded-[8px] border transition-colors ${currentPage === totalPages
                  ? "bg-background-primary border-border-primary text-text-secondary cursor-not-allowed"
                  : "bg-white border-border-primary text-text-primary hover:bg-[#f5f5f5]"
                  }`}
              >
                <span className="font-['Noto_Sans:Display_SemiBold',_sans-serif] font-semibold text-sm">
                  Next
                </span>
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M6 4L10 8L6 12"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            </div>

            {/* Page Info */}
            <div className="text-center mb-8">
              <span className="font-['Noto_Sans:Regular',_sans-serif] font-normal text-text-secondary text-sm">
                Showing {filteredCases.length > 0 ? 1 : 0} to{" "}
                {filteredCases.length} of {filteredCases.length} cases
              </span>
            </div>
          </>
        )}

        {/* Count - Only show when using infinite scroll */}
        {useInfiniteScroll && filteredCases?.length > 0 && (
          <div className="text-right py-0">
            <span className="font-['Noto_Sans:Display_Bold',_sans-serif] font-bold text-black text-sm">
              COUNT: {filteredCases.length}
            </span>
          </div>
        )}

        {/* End of Data Indicator for Infinite Scroll */}
        {useInfiniteScroll &&
          !hasMoreData &&
          !isLoadingMore &&
          !loading &&
          cases?.length > 0 && (
            <div className="text-center py-2">
              <p className="text-gray-500 text-sm">
                You've reached the end of the list
              </p>
            </div>
          )}

        {/* Popup Image */}
        {showPopup && (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
            onClick={() => setShowPopup(false)}
          >
            <div className="bg-white rounded-[8px] p-4 max-w-md mx-4">
              <img
                src={popupImage}
                alt="Case detail"
                className="w-full h-auto rounded-[8px]"
              />
              <button
                className="mt-4 w-full bg-primary text-white py-2 px-4 rounded-[8px] hover:bg-[#2680c7] transition-colors"
                onClick={() => setShowPopup(false)}
              >
                Close
              </button>
            </div>
          </div>
        )}

        <WarningsModal
          isOpen={showAllWarningsModal}
          warnings={allWarningsData}
          onClose={closeAllWarningsModal}
        />
      </div>
    </div>
  );
}
